import { Routes, Route, useNavigate } from "react-router-dom";
import { useEffect, useRef, Suspense, lazy } from "react";
import Navbar from "./component/Navbar";
import Footer from "./component/Footer";
import Home from "./component/Home";
import Seo from "./component/Seo";
import ScrollToTop from "./component/ScrollToTop";
import FraudTicker from "./component/FraudTicker";
// LoanCalculator/WhyChooseUs/HowItWorks are also statically imported (and
// eagerly needed) by Home.tsx — lazy-wrapping them here would not save any
// bytes on first load (they'd still end up in the main chunk via Home) and
// Vite flags the mixed static+dynamic import as a no-op, so they stay static.
import LoanCalculator from "./component/LoanCalculator";
import WhyChooseUs from "./component/WhyChooseUs";
import HowItWorks from "./component/HowItWorks";

// Blog routes are intentionally left as static, eager imports — untouched
// per instruction, no SEO/code-splitting work applied to blog pages.
import Blogs from "./component/Blogs";
import BlogDetail from "./component/BlogDetail";

// Route-based code splitting: everything below is only downloaded when a
// visitor actually navigates to it, instead of bundled into the initial
// page load. Home stays a static import above since it's the entry point
// for the large majority of visits.
const TermsConditions = lazy(() => import("./component/TermsConditions"));
const AboutUs = lazy(() => import("./component/AboutUs"));
const PrivacyPolicy = lazy(() => import("./component/policies/PrivacyPolicy"));
const FAQ = lazy(() => import("./component/FAQ"));
const ContactUs = lazy(() => import("./component/ContactUs"));
const InterestRatePolicy = lazy(() => import("./component/policies/InterestRatePolicy"));
const RiskManagementPolicy = lazy(() => import("./component/policies/RiskManagementPolicy"));
const CancellationRefundPolicy = lazy(() => import("./component/policies/CancellationRefundPolicy"));
const FairPracticeCode = lazy(() => import("./component/FairPracticeCode"));
const CorporateGovernance = lazy(() => import("./component/CorporateGovernance"));
const KYCAmlPolicy = lazy(() => import("./component/policies/KYCAmlPolicy"));
const LoanIllustrationAndRates = lazy(() => import("./component/LoanIllustrationAndRates"));
const GrievanceRedressal = lazy(() => import("./component/GrievanceRedressal"));
// Lazy so framer-motion (chatbot-only) stays out of the initial bundle.
const ChatBot = lazy(() => import("./component/ChatBot"));

const ExternalRedirect = ({ to }: { to: string }) => {
  const navigate = useNavigate();
  const hasOpened = useRef(false);

  useEffect(() => {
    if (!hasOpened.current) {
      hasOpened.current = true;
      window.open(to, "_blank");
      navigate("/", { replace: true });
    }
  }, [to, navigate]);
  return null;
};


function App() {
  return (
    <>
    <ScrollToTop />
    <Suspense fallback={null}>
    <Routes>
        <Route path="/" element={<Home />} />

        <Route path="/about" element={<AboutUs />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/repay" element={<ExternalRedirect to={`${import.meta.env.VITE_WEBSITE_LINK}/repay-now`} />} />
        <Route path="/login" element={<ExternalRedirect to={`${import.meta.env.VITE_WEBSITE_LINK}/login`} />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route path="/blogs/:slug" element={<BlogDetail />} />
        <Route path="/loan-details" element={<LoanIllustrationAndRates />} />
        <Route path="/loan-calculator" element={<><Navbar /><Seo title="Loan Calculator" description="Calculate your EMI, interest, and repayment schedule instantly with the EazyPaisa loan calculator." path="/loan-calculator" /><div className="pt-[72px] xl:pt-[88px]"><LoanCalculator /></div><Footer /></>} />
        <Route path="/why-choose-us" element={<><Navbar /><Seo title="Why Choose Us" description="See why thousands of salaried individuals trust EazyPaisa for fast, secure, and transparent personal loans." path="/why-choose-us" /><div className="pt-[72px] xl:pt-[88px]"><WhyChooseUs /></div><Footer /></>} />
        <Route path="/how-it-works" element={<><Navbar /><Seo title="How It Works" description="Get your EazyPaisa personal loan in 4 simple steps — register, complete KYC, get approved, receive funds." path="/how-it-works" /><div className="pt-[72px] xl:pt-[88px]"><HowItWorks /></div><Footer /></>} />
        <Route path="/terms-condition" element={<TermsConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/interest-rate-policy" element={<InterestRatePolicy />} />
        <Route path="/risk-management-policy" element={<RiskManagementPolicy/>} />
        <Route path="/cancellation-refund-policy" element={<CancellationRefundPolicy />} />
        <Route path="/fair-practice-code" element={<FairPracticeCode />} />
        <Route path="/corporate-governance" element={<CorporateGovernance />} />
        <Route path="/kyc-aml-policy" element={<KYCAmlPolicy />} />
        <Route path="/grievance-redressal" element={<GrievanceRedressal />} />
      </Routes>
      </Suspense>
      <Suspense fallback={null}>
        <ChatBot />
      </Suspense>
      <FraudTicker />
    </>
  );
}

export default App;
